import { Raycaster, Vector2 } from "three";

class InteractionManager {
  raycaster = new Raycaster();
  cursorState = null;
  dragStartAngle = null;
  dragStartWheelRotation = null;
  dragStartTime = null;
  buttonEl = null;
  sectionEl = null;
  dragHistory = []; // Buffer for last 3 drag movements: [{angle, time, clientPos}, ...]
  mousePos = { x: 0, y: 0 };
  resizeObserver = null;
  resizeTimeout = null;
  handleResize = null;

  prefersReducedMotion = false;

  constructor({canvas, camera, coins, sectionEl, buttonEl, sceneManager, prefersReducedMotion = false}) {
    this.canvas = canvas;
    this.camera = camera;
    this.coins = coins;
    this.sectionEl = sectionEl;
    this.buttonEl = buttonEl;
    this.sceneManager = sceneManager;
    this.prefersReducedMotion = prefersReducedMotion;
    this.canvasRect = canvas.getBoundingClientRect();
    // Raycaster needs to match coin layers (foreground layer = 2)
    this.raycaster.layers.enable(2);
  }

  updateCanvasRect() {
    this.canvasRect = this.canvas.getBoundingClientRect();
  }

  updateCursorState(state) {
    if (state === "spinning") {
      this.sectionEl.setAttribute("data-cursor", "loading");
    } else if ((state === "ready" || state === "complete") && this.cursorState) {
      this.sectionEl.setAttribute("data-cursor", this.cursorState);
    } else {
      this.sectionEl.removeAttribute("data-cursor");
    }
  }

  checkCoinIntersection(clientX, clientY) {
    // Update canvas rect to ensure accurate coordinates
    this.updateCanvasRect();
    const rect = this.canvasRect;
    const x = ((clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(new Vector2(x, y), this.camera);

    // Get coins from sceneManager (coins may not be initialized in constructor)
    const coins = this.sceneManager?.coins || this.coins;
    if (!coins || coins.length === 0) {
      return false;
    }

    const coinContainers = coins.map(coin => coin.container);
    const intersects = this.raycaster.intersectObjects(coinContainers, true);

    return intersects.length > 0;
  }

  getAngleFromCenter(x, y) {
    return Math.atan2(y, x);
  }

  syncRotationWithCursor(clientX, clientY, wheel) {
    const rect = this.canvas.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((clientY - rect.top) / rect.height) * 2 + 1;

    const currentAngle = this.getAngleFromCenter(x, y);
    const currentTime = performance.now();

    if (this.dragStartAngle !== null && this.dragStartWheelRotation !== null) {
      let angleDelta = currentAngle - this.dragStartAngle;

      // Normalize angle delta to [-PI, PI] range
      if (angleDelta > Math.PI) {
        angleDelta -= 2 * Math.PI;
      } else if (angleDelta < -Math.PI) {
        angleDelta += 2 * Math.PI;
      }

      // Update wheel rotation (reverse direction to match cursor movement)
      wheel.setRotation(this.dragStartWheelRotation + angleDelta);
    }

    // Add current position to drag history buffer (keep last 3)
    this.dragHistory.push({
      angle: currentAngle,
      time: currentTime,
      clientPos: { x: clientX, y: clientY },
    });

    // Keep only the last 3 movements
    if (this.dragHistory.length > 3) {
      this.dragHistory.shift();
    }
  }

  startDrag(clientX, clientY, wheel) {
    const rect = this.canvas.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((clientY - rect.top) / rect.height) * 2 + 1;
    const startAngle = this.getAngleFromCenter(x, y);
    const startTime = performance.now();

    this.dragStartAngle = startAngle;
    this.dragStartTime = startTime;
    this.dragStartWheelRotation = wheel.getRotation();

    // Initialize drag history with starting position
    this.dragHistory = [{
      angle: startAngle,
      time: startTime,
      clientPos: { x: clientX, y: clientY },
    }];
  }

  endDrag(clientX, clientY, wheel, state = "ready") {
    let spinSpeed = 0;

    // Add final position to history if not already there
    if (this.dragHistory.length > 0) {
      const lastEntry = this.dragHistory[this.dragHistory.length - 1];
      const rect = this.canvas.getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((clientY - rect.top) / rect.height) * 2 + 1;
      const finalAngle = this.getAngleFromCenter(x, y);

      // Only add if it's different from the last entry
      if (lastEntry.clientPos.x !== clientX || lastEntry.clientPos.y !== clientY) {
        this.dragHistory.push({
          angle: finalAngle,
          time: performance.now(),
          clientPos: { x: clientX, y: clientY },
        });

        // Keep only the last 3
        if (this.dragHistory.length > 3) {
          this.dragHistory.shift();
        }
      }
    }

    // Calculate velocity from average of last movements (need at least 2 points)
    if (this.dragHistory.length >= 2) {
      const velocities = [];

      // Calculate velocity from each consecutive pair
      for (let i = 1; i < this.dragHistory.length; i++) {
        const prev = this.dragHistory[i - 1];
        const curr = this.dragHistory[i];

        let angleDelta = curr.angle - prev.angle;

        // Normalize angle delta
        if (angleDelta > Math.PI) {
          angleDelta -= 2 * Math.PI;
        } else if (angleDelta < -Math.PI) {
          angleDelta += 2 * Math.PI;
        }

        // Calculate time delta in seconds
        const timeDelta = (curr.time - prev.time) / 1000;

        // Calculate velocity for this pair
        if (timeDelta > 0) {
          const angularVelocityRadPerSec = angleDelta / timeDelta;
          velocities.push(angularVelocityRadPerSec);
        }
      }

      // Average the velocities
      if (velocities.length > 0) {
        const avgAngularVelocity = velocities.reduce((sum, v) => sum + v, 0) / velocities.length;
        // Negate to match drag direction: when dragging, wheelRotation increases with positive angleDelta,
        // but when spinning, wheelRotation decreases with positive spinsPerSecond
        spinSpeed = -(avgAngularVelocity / (2 * Math.PI));
      }
    }

    this.dragStartAngle = null;
    this.dragStartWheelRotation = null;
    this.dragStartTime = null;
    this.dragHistory = [];

    wheel.setSpinSpeed(spinSpeed);
  }

  setupEventListeners(sceneManager, spinToWin) {
    // Debounce resize handler for better performance
    this.handleResize = () => {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = setTimeout(() => {
        // Mark canvas size for update instead of updating directly
        // This prevents race conditions by updating in the animation loop
        spinToWin.markCanvasSizeNeedsUpdate();
      }, 16); // ~60fps throttle
    };

    window.addEventListener("resize", this.handleResize);

    // Use ResizeObserver for more accurate container resize detection
    if (this.canvas && window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        // Mark canvas size for update instead of updating directly
        spinToWin.markCanvasSizeNeedsUpdate();
      });
      this.resizeObserver.observe(this.canvas);
    }

    this.buttonEl.addEventListener("click", () => {
      if (spinToWin.state === "ready") {
        spinToWin.handleShouldStartSpinning(true);
      }
    });

    this.sectionEl.addEventListener("mousemove", (e) => {
      // Update canvas rect to ensure accurate coordinates
      this.updateCanvasRect();
      const rect = this.canvasRect;
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      this.mousePos = { x, y };

      // Always track mouse position for coin hover effects, even when spinning
      // But skip hover effects if user prefers reduced motion
      const state = spinToWin.getState();

      // Handle interaction in ready and complete states
      // Skip hover effects if user prefers reduced motion
      if ((state === "ready" || state === "complete") && !this.prefersReducedMotion) {
        if (this.cursorState === "grabbing") {
          this.syncRotationWithCursor(e.clientX, e.clientY, spinToWin.wheel);
        } else {
          const wasHovering = this.cursorState === "grab";
          const isHovering = this.checkCoinIntersection(e.clientX, e.clientY);
          if (wasHovering !== isHovering) {
            this.cursorState = isHovering ? "grab" : null;
            this.updateCursorState(state);
          }
        }
      }
    });

    this.sectionEl.addEventListener("mouseleave", () => {
      const state = spinToWin.getState();

      if (state === "ready" || state === "complete") {
        // Only end drag if we're actually dragging and not spinning
        if (this.cursorState === "grabbing" && this.dragHistory.length > 0) {
          // Use the last known drag position for velocity calculation
          const lastPos = this.dragHistory[this.dragHistory.length - 1].clientPos;
          this.endDrag(lastPos.x, lastPos.y, spinToWin.wheel, state);
          this.cursorState = null;
          this.updateCursorState(state);
        } else if (this.cursorState !== "grabbing") {
          // Just reset cursor state if not dragging
          this.cursorState = null;
          this.updateCursorState(state);
        }
      }
      this.mousePos = { x: 0, y: 0 };
    });

    this.sectionEl.addEventListener("pointerdown", (e) => {
      const state = spinToWin.getState();
      // Allow interaction in ready and complete states
      if (state === "ready" || state === "complete") {
        // Check intersection and start drag if over a coin
        if (this.checkCoinIntersection(e.clientX, e.clientY)) {
          e.preventDefault(); // Prevent default to ensure proper drag handling
          this.sectionEl.setPointerCapture(e.pointerId); // Capture pointer for drag
          this.cursorState = "grabbing";
          this.startDrag(e.clientX, e.clientY, spinToWin.wheel);
          this.updateCursorState(state);
        }
      }
    });

    this.sectionEl.addEventListener("pointermove", (e) => {
      const state = spinToWin.getState();
      if ((state === "ready" || state === "complete") && this.cursorState === "grabbing") {
        // Update mouse position and sync rotation during drag
        this.updateCanvasRect();
        const rect = this.canvasRect;
        const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
        this.mousePos = { x, y };
        this.syncRotationWithCursor(e.clientX, e.clientY, spinToWin.wheel);
      }
    });

    this.sectionEl.addEventListener("pointerup", (e) => {
      const state = spinToWin.getState();
      if (state === "ready" || state === "complete") {
        if (this.cursorState === "grabbing") {
          this.sectionEl.releasePointerCapture(e.pointerId); // Release pointer capture
        }
        this.endDrag(e.clientX, e.clientY, spinToWin.wheel, state);

        if (this.checkCoinIntersection(e.clientX, e.clientY)) {
          this.cursorState = "grab";
        } else {
          this.cursorState = null;
        }
        this.updateCursorState(state);
      }
    });

    this.sectionEl.addEventListener("pointercancel", (e) => {
      const state = spinToWin.getState();
      if ((state === "ready" || state === "complete") && this.cursorState === "grabbing") {
        this.sectionEl.releasePointerCapture(e.pointerId);
        // Use last known position if available
        if (this.dragHistory.length > 0) {
          const lastPos = this.dragHistory[this.dragHistory.length - 1].clientPos;
          this.endDrag(lastPos.x, lastPos.y, spinToWin.wheel, state);
        } else {
          this.endDrag(e.clientX, e.clientY, spinToWin.wheel, state);
        }
        this.cursorState = null;
        this.updateCursorState(state);
      }
    });
  }

  getCursorState() {
    return this.cursorState;
  }

  getMousePos() {
    return this.mousePos;
  }
}

export default InteractionManager;

